import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Platform } from '@app/classes';
import { ConfirmationDialogComponent } from '@app/components';
import { AnalyticEvents } from '@app/enums';
import { JobPostingMenuService } from '@app/modules/applicant-tracker/services/menus/job-posting-menu.service';
import { HiringPermissionsService, SettingsIntegrationService } from '@applicant-tracker/services';
import { JobPosting } from '@models/recruiting/job-posting.model';
import { AnalyticService, AuthService, NotifyService } from 'app/services';
import { format } from 'date-fns';

@Component({
    selector: 'app-job-posting-header',
    templateUrl: './header.template.html',
    styleUrls: ['./header.style.scss'],
})
export class HeaderView implements OnInit {
    @ViewChild('confirmArchiveDialog', { static: true }) confirmArchiveDialog!: ConfirmationDialogComponent;
    @Input() jobPosting: JobPosting;
    @Input() showApplicantMessagingButton = false;
    @Output() onChangePause: EventEmitter<boolean> = new EventEmitter();
    @Output() onArchiveChange: EventEmitter<boolean> = new EventEmitter();

    isOpenPauseModal = false;
    canArchive = true;

    constructor(
        private notify: NotifyService,
        private auth: AuthService,
        private router: Router,
        public route: ActivatedRoute,
        private analyticService: AnalyticService,
        private hiringPermissionsService: HiringPermissionsService,
        public jobPostingMenuService: JobPostingMenuService,
        private integrationService: SettingsIntegrationService
    ) {}

    async ngOnInit(): Promise<void> {
        await this.hiringPermissionsService.handle(this.jobPosting);

        this.canArchive = this.auth.can(Platform.recruiting.permission.recruiter);
    }

    onArchive(): void {
        this.analyticService.trackEvent(AnalyticEvents.RecruitingArchiveJob);
        this.confirmArchiveDialog.open().then((confirm) => {
            if (confirm) {
                this.jobPosting.archivedAt = format(new Date(), 'YYYY-MM-DD');
                this.jobPosting.isVisible = false;
                this.jobPosting
                    .param('company', this.auth.company!.id)
                    .save()
                    .then(() => {
                        this.onArchiveChange.emit(true);
                        this.integrationService.jobPostingStateChange$.next('archived');
                        this.notify.success('applicant-tracker.jobPostingArchived');
                    })
                    .catch((err) => {
                        this.notify.error(err.message || 'applicant-tracker.couldNotArchiveJobPosting');
                    });
            }
        });
    }

    onUnarchive(): void {
        this.jobPosting.archivedAt = null;
        this.jobPosting.isVisible = true;

        this.jobPosting
            .param('company', this.auth.company!.id)
            .save()
            .then(() => {
                this.onArchiveChange.emit(false);
                this.integrationService.jobPostingStateChange$.next('archived');
                this.notify.success('applicant-tracker.jobPostingUnarchived');
            })
            .catch((err) => {
                this.notify.error(err.message || 'applicant-tracker.couldNotUnarchiveJobPosting');
            });
    }

    onPause(): void {
        this.analyticService.trackEvent(AnalyticEvents.RecruitingPauseJob);
        this.onChangePause.emit(true);
        this.closePauseModal();
    }

    copyPosting(): void {
        this.analyticService.trackEvent(AnalyticEvents.RecruitingJobPostingMeatballClickDuplicate);
        this.router.navigate(['/', 'recruiting', 'jobPostings', 'create'], {
            queryParams: {
                jobPostingId: this.jobPosting.id,
            },
        });
    }

    openPauseModal(): void {
        this.isOpenPauseModal = true;
    }

    onUnpause(): void {
        this.onChangePause.emit(false);
        this.closePauseModal();
    }

    closePauseModal(): void {
        this.isOpenPauseModal = false;
    }

    /**
     * This is currently not in the UI
     * We may allow deleting job postings again in the future
     * So do not delete this code without checking with the product team
     */
    onDelete(): void {
        new ConfirmationDialogComponent().open().then((confirm) => {
            if (confirm) {
                this.jobPosting
                    .delete()
                    .then(() => {
                        this.notify.success('applicant-tracker.jobPostingHasBeenDeleted');
                        this.router.navigate(['/', 'recruiting', 'jobPostings'], { relativeTo: this.route });
                    })
                    .catch((err) => {
                        this.notify.error(err.message || 'applicant-tracker.jobPostingCouldNotBeDeleted');
                    });
            }
        });
    }
}
